/**
 * The owner's Activity tab for one property: every dated thing that has
 * happened to it, newest first, five at a time.
 *
 * Five is the page size the tab was asked for, and it suits the content: these
 * entries are read, not scanned — "who uploaded that, and when" is answered by
 * looking at one of them, so a wall of thirty is worse than a short page with a
 * pager under it. The count in the header always states the true total, so a
 * page of five never reads as "that's all there is".
 *
 * The feed itself is assembled by buildOwnerPropertyActivity, which is pure and
 * tested; this file is the binding: which queries feed it, and the pager.
 */
import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, History } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/LanguageContext";
import { OwnerActivityTimeline } from "@/components/admin/v2/OwnerActivityTimeline";
import { useOwnerPropertyDocuments } from "@/hooks/owner/useOwnerPropertyDocuments";
import {
  useOwnerPropertyDocumentHistory,
  useOwnerPropertySubmissionRow,
} from "@/hooks/owner/useOwnerPropertyActivity";
import type {
  OwnerHistoryEntry,
  OwnerRoom,
  OwnerTenant,
} from "@/hooks/owner/useOwnerPropertyDetail";
import { buildOwnerPropertyActivity } from "@/lib/owner-property-activity";

/** How many entries a page holds. */
const PAGE_SIZE = 5;

export function OwnerPropertyActivityTab({
  propertyId,
  active,
  tenants,
  history,
  apartments,
  rooms,
}: {
  propertyId: number | null;
  /** Only fetch once the tab is open — see the hooks' `enabled`. */
  active: boolean;
  tenants: OwnerTenant[];
  history: OwnerHistoryEntry[];
  apartments: Array<{ id: number; label: string }>;
  rooms: OwnerRoom[];
  /** Accepted for symmetry with the page's other tabs; the timeline formats
   *  its own timestamps through formatDateTime. */
  locale?: string;
}) {
  const { t } = useLanguage();
  const [page, setPage] = useState(0);

  const submission = useOwnerPropertySubmissionRow(propertyId, active);
  const docHistory = useOwnerPropertyDocumentHistory(propertyId, active);
  const tenantDocs = useOwnerPropertyDocuments(propertyId, active);

  const apartmentLabels = useMemo(
    () => new Map(apartments.map((a) => [a.id, a.label])),
    [apartments]
  );
  const roomTitles = useMemo(
    () =>
      new Map(
        rooms.filter((r) => r.title).map((r) => [r.id, r.title as string])
      ),
    [rooms]
  );

  const events = useMemo(
    () =>
      buildOwnerPropertyActivity({
        property: submission.data ?? null,
        documents: docHistory.data ?? [],
        tenantDocuments: tenantDocs.data?.documents ?? [],
        tenants,
        history,
        apartmentLabels,
        roomTitles,
        t,
      }),
    [
      submission.data,
      docHistory.data,
      tenantDocs.data,
      tenants,
      history,
      apartmentLabels,
      roomTitles,
      t,
    ]
  );

  const isLoading =
    submission.isLoading || docHistory.isLoading || tenantDocs.isLoading;

  const pageCount = Math.max(1, Math.ceil(events.length / PAGE_SIZE));
  /* Clamped rather than reset in an effect: the list shrinks when a refetch
     lands (a document removed elsewhere), and a page index left pointing past
     the end renders an empty tab that looks like a load failure. */
  const current = Math.min(page, pageCount - 1);
  const start = current * PAGE_SIZE;
  const visible = events.slice(start, start + PAGE_SIZE);

  return (
    <div className="rounded-2xl border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-5 py-3.5">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <History className="h-4 w-4" />
          {t("owner.detail.tabActivity", "Activity")}
          {events.length > 0 && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium tabular-nums text-muted-foreground">
              {events.length}
            </span>
          )}
        </h3>
        {events.length > PAGE_SIZE && (
          <p className="text-xs tabular-nums text-muted-foreground">
            {start + 1}–{Math.min(start + PAGE_SIZE, events.length)}{" "}
            {t("owner.activity.of", "of")} {events.length}
          </p>
        )}
      </div>

      <div className="p-5">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-2 rounded-xl border p-3">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            ))}
          </div>
        ) : (
          <OwnerActivityTimeline
            events={visible}
            emptyTitle={t("owner.activity.emptyTitle", "No activity yet")}
            emptyDescription={t(
              "owner.activity.emptyDesc",
              "Documents, approvals, move-ins and rent payments appear here as they happen."
            )}
          />
        )}

        {!isLoading && pageCount > 1 && (
          <div className="mt-4 flex items-center justify-between gap-3 border-t pt-3">
            <button
              type="button"
              onClick={() => setPage(Math.max(0, current - 1))}
              disabled={current === 0}
              className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-accent disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              {t("owner.activity.previous", "Previous")}
            </button>
            <span className="text-xs tabular-nums text-muted-foreground">
              {t("owner.activity.page", "Page")} {current + 1}/{pageCount}
            </span>
            <button
              type="button"
              onClick={() => setPage(Math.min(pageCount - 1, current + 1))}
              disabled={current >= pageCount - 1}
              className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-accent disabled:opacity-40 disabled:hover:bg-transparent"
            >
              {t("owner.activity.next", "Next")}
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
